import React, { ReactNode } from "react";
import {
  AccountSettingsStore,
  CountryStore,
  PurchaseStore,
  RolesAndPermissionStore,
} from "../../shared/store";
import {
  RelatedItemsPriceCategoriesModel,
  PurchasePaymentIntentModel,
  PriceTermSettings,
  StandardPricingModel,
  DonationPricingPageInputsModel,
  CustomerFacingCheckOutItemModel,
  PurchaseCheckOutDetails,
  PurchaseCheckOutAddOnDetails,
  CustomerFacingCheckOutModel,
  PurchasePaymentModel,
} from "../../shared/models";
import { observer } from "mobx-react";
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  Params,
} from "react-router-dom";
import { Subject, takeUntil } from "rxjs";
import PurchaseProtectedRoute from "./PurchaseProtectedRoute";
import PurchaseAddOn from "./PurchaseAddOn";
import PurchaseOptions from "./PurchaseOptions";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  IAPIResponse,
  Input,
  Loader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CheckOutService, PurchaseLinkService } from "../../shared/services";
import {
  IAPIPurchaseCheckOutItemsModel,
  IAPIPurchaseMerchantModel,
} from "../../shared/interfaces";
import {
  ImagePlacementTypes,
  PricingModel,
  RecurringBillingInterval,
  UpsertPurchaseActiveStep,
} from "../../shared/enums";
import {
  DonationItem,
  MultipleItems,
  PurchasePayment,
  PurchaseSection,
  PurchaseSuccess,
  PurchaseTopBar,
  SingleItemMultiplePrices,
} from "../../shared/components";
import {
  PurchaseColoredBackground,
  PurchaseWrapper,
} from "../../shared/styles";
import { NMIPaymentMethodFormHandle } from "../../shared/components/NMIPaymentMethodForm";

interface IProps {
  location: Location;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  purchaseStore: PurchaseStore;
  countryStore: CountryStore;
  isPurchaseLink: boolean;
  fundraiserId?: string;
  isRenderFromFundraiser: boolean;
  customerFacingCheckOutDetails?: CustomerFacingCheckOutModel;
  rolesAndPermissionStore: RolesAndPermissionStore;
  promoterId?: string;
}

type IState = {
  isPaymentInProgress: boolean;
  isCountryLoaded: boolean;
};

class PurchaseRoutes extends React.Component<IProps, IState> {
  private loader: Loader = new Loader(true);
  private paymentLoader: Loader = new Loader(false);
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private isPaymentInProgress: boolean = false;

  formRef = React.createRef<NMIPaymentMethodFormHandle>();
  constructor(props: IProps) {
    super(props);
    this.state = {
      isPaymentInProgress: false,
      isCountryLoaded: false,
    };
    this.loadData();
  }

  setDefaultCountry() {
    if (this.state.isCountryLoaded) {
      return;
    }
    this.setState({ isCountryLoaded: true });
    const usCountry = this.props.countryStore.getUSCountry();
    if (usCountry) {
      this.purchaseStore.initPurchaseSectionData(usCountry);
    }
  }

  loadData() {
    const usCountry = this.props.countryStore.getUSCountry();
    if (usCountry) {
      this.purchaseStore.initPurchaseSectionData(usCountry);
    }

    const id = this.checkOutId;
    if (this.props.isRenderFromFundraiser) {
      this.purchaseStore.updateCustomerFacingCheckOutModel(
        this.props.customerFacingCheckOutDetails,
      );
      this.loader.hide();
      AccountSettingsStore.isLoadingInProgress = false;
      return;
    }
    if (this.props.isPurchaseLink) {
      PurchaseLinkService.getPurchaseLink("", id)
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (response) => {
            if (response.result) {
              this.purchaseStore.deserializePurchaseLinkToCustomerFacing(
                response.result,
              );
              // Load account settings with the correct account ID from the response
              AccountSettingsStore.loadGeneralSettings(
                response.result.accountId,
              );
            }
            this.loader.hide();
          },
          error: () => {
            ToastService.showError("Error in loading purchase link details");
            this.loader.hide();
          },
        });
    } else {
      CheckOutService.getCustomerFacingCheckOutDetails(id || "")
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (result) => {
            if (!result.result) {
              ToastService.showError("Error in loading checkout details");
            } else {
              this.purchaseStore.deserializeCustomerFacingCheckOutModel(
                result.result,
              );
              // Load account settings with the correct account ID from the response
              AccountSettingsStore.loadGeneralSettings(result.result.accountId);
            }
            this.loader.hide();
          },
          error: () => {
            ToastService.showError("Error in loading checkout details");
            this.loader.hide();
          },
        });
    }
  }

  get checkOutId() {
    if (this.props.isRenderFromFundraiser) {
      return this.props.fundraiserId || null;
    }
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/",
    );
    const id =
      split[split.length - 1] === "success"
        ? split[split.length - 2]
        : split[split.length - 1];

    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }
  get purchaseStore(): PurchaseStore {
    return this.props.purchaseStore || new PurchaseStore();
  }

  onPurchaseClick = (
    item: CustomerFacingCheckOutItemModel,
    quantity?: number,
  ) => {
    const clonedPurchaseDetails = new PurchaseCheckOutDetails(
      this.purchaseStore.checkOutPurchaseDetails || undefined,
    );
    if (item.isTrackInventory) {
      //only select active variant index.
      // if (item.hasVariants) {
      //     const variantIndex = item.variants?.options?.findIndex(
      //         (x) => x.isRemoved != true && x.onHand && parseFloat(x.onHand) > 0,
      //     );
      //     if (variantIndex !== -1) {
      //         item.selectedVariantIndex = variantIndex;
      //     }
      // }
    }
    if (
      item.includedItemsByCondition &&
      item.includedItemsByCondition.length === 0
    ) {
      item.includedItemsByCondition = item.getAllowedIncludedItems();
    }

    clonedPurchaseDetails.item = item;
    clonedPurchaseDetails.addOns = [];
    clonedPurchaseDetails.activeItemIndex = -1;
    if (quantity) {
      clonedPurchaseDetails.item.quantity = quantity;
    }

    // const clonedRequiredItems = item.requiredItems?.map((r) => new RelatedItemsCategoriesModel(r)) || [];
    // const addOns: PurchaseCheckOutAddOnDetails[] = clonedPurchaseDetails.addOns || [];

    // if (clonedRequiredItems.length > 0 && addOns.length === 0) {
    //     clonedRequiredItems.map((r) => {
    //         if (r.requiredVariantIds?.length > 0) {
    //             const requiredOptions = r.variants?.options?.filter(
    //                 (o) => indexOf(r.requiredVariantIds, o.id) !== -1,
    //             );

    //             const clonedVariant = new ProductAttributeInputModel({ ...r, options: requiredOptions });
    //             const price = r.prices?.find((p) => p.isDefaultPrice) || r.prices?.[0];
    //             const newAddOn = new PurchaseCheckOutAddOnDetails({
    //                 ...price,
    //                 id: r.id,
    //                 name: r.name,
    //                 itemId: r.id,
    //                 itemType: r.type,
    //                 variantId: r.requiredVariantIds[0],
    //                 quantity: 1,
    //                 selectedVariant: clonedVariant,
    //                 hasVariants: r.hasVariants,
    //                 onlyAllowedVariantIds: r.requiredVariantIds,
    //             });
    //             addOns.push(newAddOn);
    //             // return r.requiredVariantIds?.map((v) => {
    //             //     //id here is the id of product/membership or service
    //             //     const matchedAddOn = addOns.find((x) => x.variantId === v);
    //             //     if (!matchedAddOn) {
    //             //         const newAddOn = new PurchaseCheckOutAddOnDetails({
    //             //             id: r.id,
    //             //             name: r.name,
    //             //             itemId: r.id,
    //             //             itemType: r.type,
    //             //             variantId: v,
    //             //             quantity: 1,
    //             //             selectedVariant: r.variants,
    //             //             hasVariants: r.hasVariants,
    //             //         });
    //             //         addOns.push(newAddOn);
    //             //     }
    //             // });
    //         } else {
    //             const price = r.prices?.find((p) => p.isDefaultPrice) || r.prices?.[0];
    //             const newAddOn = new PurchaseCheckOutAddOnDetails({
    //                 ...price,
    //                 name: r.name,
    //                 quantity: 1,
    //                 itemId: r.id,
    //                 itemType: r.type,
    //                 hasVariants: r.hasVariants,
    //                 selectedVariant: r.variants,
    //                 variantId: r.variants?.options?.[0]?.id || '',
    //             });
    //             addOns.push(newAddOn);
    //         }
    //     });
    // }
    // clonedPurchaseDetails.addOns = addOns;
    this.purchaseStore.updateCheckOutPurchaseDetails(clonedPurchaseDetails);

    const hasError = this.purchaseStore.checkOutPurchaseDetails?.item.hasError(
      "customPricingInputValue",
    );
    if (hasError) return;
    this.handleBackOrNextStep(true);
  };

  updateAddOns(addOns: PurchaseCheckOutAddOnDetails[]) {
    const clonedPurchaseDetails = new PurchaseCheckOutDetails(
      this.purchaseStore.checkOutPurchaseDetails || undefined,
    );

    clonedPurchaseDetails.addOns = addOns;
    this.purchaseStore.updateCheckOutPurchaseDetails(clonedPurchaseDetails);
  }

  // updateOptionsPageData = (data: OptionSectionModel) => {
  //     this.purchaseStore.updateOptionsPageData(new OptionSectionModel(data));
  // };

  onPayClick(hasValidNMIPaymentMethod = false) {
    if (this.state.isPaymentInProgress) return;

    //ONLY DO IT FOR NMI AS FOR NMI WE NEED TO WAIT FOR PAYMENT TOKEN AFTER validateCardDetails
    if (
      !hasValidNMIPaymentMethod &&
      this.purchaseStore.customerFacingCheckOutDetails.merchantAccount?.isNMIMerchantAccount()
    ) {
      this.formRef.current?.validateCardDetails();
      return;
    }
    const includeSurchargeOnApplyPromo = this.purchaseStore.paymentSectionData
      .promotionRelatedData
      ? !this.purchaseStore.paymentSectionData.promotionRelatedData
          .waiveSurcharge
      : true;

    this.setState({ isPaymentInProgress: true });
    const request = this.purchaseStore.createCheckOutAPIPurchaseModel(
      this.checkOutId,
      // Non-zero percentage flags coverProcessingCost=true so the server applies the surcharge it
      // authoritatively recomputes from the checkout/link's own stored percentage.
      this.includeSurcharge() && includeSurchargeOnApplyPromo
        ? this.surchargePercentage()
        : 0,
      this.purchaseStore.paymentSectionData.promotionRelatedData || undefined,
      this.props.isRenderFromFundraiser,
      this.props.promoterId,
    );

    const handleSuccess = async (
      result: IAPIResponse<IAPIPurchaseCheckOutItemsModel>,
    ) => {
      if (result.result?.success) {
        try {
          const isDonation =
            this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly;
          const donationFrequency = this.purchaseStore
            .customerFacingCheckOutDetails.donationPricingPageData?.oneTime
            ? "One Time"
            : "Monthly";
          await this.purchaseStore.setPurchaseDoneResponse(
            result.result,
            isDonation,
            donationFrequency,
          );
          await this.purchaseStore.togglePurchaseDone();
          const navigateUrl = `/app/purchase/${this.props.isPurchaseLink ? "purchase-link" : "checkout"}/success/${this.checkOutId}`;
          this.navigateToURL(navigateUrl, UpsertPurchaseActiveStep.Success);
        } catch (error) {
          this.setState({ isPaymentInProgress: false });
          ToastService.showError("Error in processing the purchase");
        }
      } else {
        this.setState({ isPaymentInProgress: false });
        ToastService.showError("Error in purchasing items");
      }
    };

    const handleError = () => {
      this.setState({ isPaymentInProgress: false });
      ToastService.showError("Error in purchasing items");
    };

    if (this.props.isPurchaseLink) {
      PurchaseLinkService.purchaseLinkCheckoutItems(request).subscribe({
        next: handleSuccess,
        error: handleError,
      });
    } else {
      CheckOutService.purchaseCheckOutItems(request).subscribe({
        next: handleSuccess,
        error: handleError,
      });
    }
  }

  updateDonationPricingPageData(
    donationPricingPageData: DonationPricingPageInputsModel,
  ) {
    this.purchaseStore.updateCustomerFacingCheckOutModel(
      new CustomerFacingCheckOutModel({
        ...this.purchaseStore.customerFacingCheckOutDetails,
        donationPricingPageData: donationPricingPageData,
      }),
    );
  }

  onDonationClick = () => {
    const {
      oneTime,
      donationAmout,
      customAmount,
      allowAnonymousDonation,
      annualIncrease,
      annualIncreasePercentage,
      coverProcessingCost,
      offerInMemoryOf,
      inMemoryOfComment,
      inMemoryOfName,
      selectedBeneficiaries,
      coverProcessingCostDescription,
    } =
      this.purchaseStore.customerFacingCheckOutDetails.donationPricingPageData;
    const item =
      this.purchaseStore.customerFacingCheckOutDetails.items?.length > 0
        ? this.purchaseStore.customerFacingCheckOutDetails.items[0]
        : null;
    const isValid =
      this.purchaseStore.customerFacingCheckOutDetails.donationPricingPageData.validateForCustomerFacing();
    if (!isValid) return;
    const donationAmount =
      donationAmout > 0 ? donationAmout : parseFloat(customAmount);
    const priceModel = new RelatedItemsPriceCategoriesModel({
      terms: new PriceTermSettings({
        initialPayment: donationAmount,
        totalPayments: donationAmount,
        billingInterval: oneTime
          ? RecurringBillingInterval.Day
          : RecurringBillingInterval.Month,
        billingIntervalCount: 1,
        pricingModel: PricingModel.Standard,
        standardPricing: new StandardPricingModel({
          originalPrice: donationAmount,
          price: donationAmount,
          cost: donationAmount,
        }),
      }),
    });

    const hasAmountChange =
      donationAmount !==
      this.purchaseStore.checkOutPurchaseDetails?.item?.donationAmount;
    const donationItems = new CustomerFacingCheckOutItemModel({
      ...item,
      id:
        item?.itemId ||
        this.purchaseStore.customerFacingCheckOutDetails.donationDetailPage
          .category?.id ||
        "",
      name:
        item?.name ||
        this.purchaseStore.customerFacingCheckOutDetails.donationDetailPage
          .name,
      quantity: 1,
      priceInNumber: 1,
      price: priceModel,
      donationAmount: donationAmount,
      payMonthly: !oneTime,
      inMemoryOf: offerInMemoryOf ?? false,
      tributeMessage: inMemoryOfComment,
      dedicatedTo: inMemoryOfName,
      beneficiaries: selectedBeneficiaries,
      allowAnnualIncrease: annualIncrease ?? false,
      annualIncreasePercentage: annualIncreasePercentage,
      anonymousDonation: allowAnonymousDonation ?? false,
      coverFee: coverProcessingCost ?? false,
      coverFeeMessage: coverProcessingCostDescription,
      requiredItems: item?.requiredItems || [],
      recommendedItems: item?.recommendedItems || [],
      includedItems: item?.includedItems || [],
      //   recommendedItems: [
      //     new RelatedItemsCategoriesModel({
      //       prices: [priceModel],
      //     }),
      //   ],
    });

    if (hasAmountChange) {
      donationItems.includedItemsByCondition =
        donationItems.getAllowedIncludedItems();
    } else {
      donationItems.includedItemsByCondition =
        this.purchaseStore.checkOutPurchaseDetails?.item
          ?.includedItemsByCondition;
    }

    this.purchaseStore.updateCheckOutPurchaseDetails(
      new PurchaseCheckOutDetails({
        ...this.purchaseStore.checkOutPurchaseDetails,
        addOns: hasAmountChange
          ? []
          : this.purchaseStore.checkOutPurchaseDetails?.addOns,
        activeItemIndex: 0,
        item: donationItems,
      }),
    );
    this.handleBackOrNextStep(true);
  };

  // updateItem(item: CustomerFacingCheckOutItemModel) {
  //     const clonedPurchaseDetails = new PurchaseCheckOutDetails(
  //         this.purchaseStore.checkOutPurchaseDetails || undefined,
  //     );
  //     clonedPurchaseDetails.item = new CustomerFacingCheckOutItemModel(item);
  //     this.purchaseStore.updateCheckOutPurchaseDetails(clonedPurchaseDetails);
  // }

  updateItems(items: CustomerFacingCheckOutItemModel[]) {
    const clonedPurchaseDetails = new CustomerFacingCheckOutModel({
      ...this.purchaseStore.customerFacingCheckOutDetails,
      items: items,
    });
    this.purchaseStore.updateCustomerFacingCheckOutModel(clonedPurchaseDetails);
  }

  get classBasedOnActiveStep() {
    switch (this.purchaseStore.activeStep) {
      case UpsertPurchaseActiveStep.Purchase:
        return "";
      case UpsertPurchaseActiveStep.AddOn:
        return "purchase-addon-screen";
      case UpsertPurchaseActiveStep.Options:
        return "purchase-options-screen";
      case UpsertPurchaseActiveStep.Payment:
        return "purchase-payment-section";
      default:
        return "";
    }
  }

  get purchaseSectionComponent() {
    const isDonationOnly =
      this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly;
    switch (this.purchaseStore.customerFacingCheckOutDetails.displayType) {
      case "SingleItemWithMultiplePrices":
        return (
          <SingleItemMultiplePrices
            purchaseStore={this.purchaseStore}
            onPurchaseClick={(item: CustomerFacingCheckOutItemModel) =>
              this.onPurchaseClick(item)
            }
            items={this.purchaseStore.customerFacingCheckOutDetails.getSortedItems()}
            recommendedItem={
              this.purchaseStore.customerFacingCheckOutDetails.recommendedItem
            }
            color={this.purchaseStore.customerFacingCheckOutDetails.getColors()}
            buttonCaption={this.purchaseStore.customerFacingCheckOutDetails.getButtonCaption()}
            buttonStyle={this.purchaseStore.customerFacingCheckOutDetails.getButtonStyle()}
            updateItems={(items: CustomerFacingCheckOutItemModel[]) =>
              this.updateItems(items)
            }
            customOption={
              this.purchaseStore.customerFacingCheckOutDetails.customOption
            }
            allowIncludedItems={
              this.purchaseStore.customerFacingCheckOutDetails
                .allowIncludedItems
            }
          />
        );
      case "MultipleItems":
        return (
          <MultipleItems
            recommendedItem={
              this.purchaseStore.customerFacingCheckOutDetails.recommendedItem
            }
            purchaseStore={this.purchaseStore}
            items={this.purchaseStore.customerFacingCheckOutDetails.getSortedItems()}
            onPurchaseClick={(item: CustomerFacingCheckOutItemModel) =>
              this.onPurchaseClick(item)
            }
            groupByBillingFrequency={
              this.purchaseStore.customerFacingCheckOutDetails
                .groupByBillingFrequency
            }
            colors={this.purchaseStore.customerFacingCheckOutDetails.getColors()}
            buttonCaption={this.purchaseStore.customerFacingCheckOutDetails.getButtonCaption()}
            buttonStyle={this.purchaseStore.customerFacingCheckOutDetails.getButtonStyle()}
            updateItems={(items: CustomerFacingCheckOutItemModel[]) =>
              this.updateItems(items)
            }
            customOption={
              this.purchaseStore.customerFacingCheckOutDetails.customOption
            }
            allowIncludedItems={
              this.purchaseStore.customerFacingCheckOutDetails
                .allowIncludedItems
            }
          />
        );
      case "MultipleItemsInbundle":
      case "SingleItem":
        if (isDonationOnly)
          return (
            <DonationItem
              customerFacingCheckOutDetails={
                this.purchaseStore.customerFacingCheckOutDetails
              }
              updateDonationPricingPageData={(
                data: DonationPricingPageInputsModel,
              ) => this.updateDonationPricingPageData(data)}
              onDonationClick={() => this.onDonationClick()}
              isPreview={false}
            />
          );
        return (
          <PurchaseSection
            item={this.purchaseStore.customerFacingCheckOutDetails.items[0]}
            purchaseStore={this.purchaseStore}
            onPurchaseClick={(
              item: CustomerFacingCheckOutItemModel,
              quantity: number,
            ) => this.onPurchaseClick(item, quantity)}
            colors={this.purchaseStore.customerFacingCheckOutDetails.getColors()}
            buttonCaption={this.purchaseStore.customerFacingCheckOutDetails.getButtonCaption()}
            buttonStyle={this.purchaseStore.customerFacingCheckOutDetails.getButtonStyle()}
            updateItem={(item: CustomerFacingCheckOutItemModel) => {
              const clonedItems =
                this.purchaseStore.customerFacingCheckOutDetails.items.map(
                  (item) => {
                    return new CustomerFacingCheckOutItemModel(item);
                  },
                );

              clonedItems[0] = item;
              this.updateItems(clonedItems);
            }}
            allowIncludedItems={
              this.purchaseStore.customerFacingCheckOutDetails
                .allowIncludedItems
            }
          />
        );
      default:
        break;
    }
  }

  navigateToURL = async (url: string, activeStep: string) => {
    await this.purchaseStore.updateActiveStep(
      activeStep as UpsertPurchaseActiveStep,
    );
    if (this.props.isRenderFromFundraiser) {
      this.purchaseStore.updateActiveStepURL(url);
      return;
    }
    this.props.navigate(url);
  };

  /* The checkout/link setting is the only thing that decides now - items no longer carry their own
     "Allow Surcharge" flag, so there is nothing left to check per item. */

  /* A surcharge exists only when it is switched on AND has a rate. A 0% rate - a bad save, or a
     migration that could not resolve the merchant's card rate - has to behave exactly like "off":
     asking the buyer to agree to a "0% processing fee" that adds nothing is worse than saying
     nothing. */
  hasSurcharge = (): boolean => {
    const pd = this.purchaseStore.customerFacingCheckOutDetails.paymentDetails;
    return !!pd?.surchargeEnabled && this.surchargePercentage() > 0;
  };

  // The "cover the cost" checkbox only shows when the surcharge is enabled AND set to ask the
  // buyer first; when it is enabled without asking, it is applied automatically (no checkbox).
  showSurchargeCheckbox = (): boolean => {
    const pd = this.purchaseStore.customerFacingCheckOutDetails.paymentDetails;
    return this.hasSurcharge() && !!pd?.surchargeAskCustomerFirst;
  };

  includeSurcharge = (): boolean => {
    const { customerFacingCheckOutDetails, paymentSectionData } =
      this.purchaseStore;
    const pd = customerFacingCheckOutDetails.paymentDetails;
    if (!this.hasSurcharge()) return false;
    // "Ask customer first" -> only when the buyer opted in; otherwise always applied.
    return pd?.surchargeAskCustomerFirst
      ? paymentSectionData.surchargeChecked
      : true;
  };

  // Surcharge percentage configured on this checkout/link (0.1%-4%), server-authoritative.
  surchargePercentage = (): number => {
    const pd = this.purchaseStore.customerFacingCheckOutDetails.paymentDetails;
    return parseFloat(pd?.surchargePercentage?.toString() || "0") || 0;
  };

  onNewPaymentMethodIdUpdate(paymentMethodId: string) {
    this.purchaseStore.updatePaymentSectionData(
      new PurchasePaymentModel({
        ...this.props.purchaseStore.paymentSectionData,
        paymentMethodId: paymentMethodId,
      }),
    );
    if (
      this.purchaseStore.customerFacingCheckOutDetails.merchantAccount?.isNMIMerchantAccount()
    ) {
      this.onPayClick(true);
    }
  }

  get routes() {
    const isPaymentPage =
      this.purchaseStore.activeStep === UpsertPurchaseActiveStep.Payment;
    const showBackButton =
      !this.purchaseStore.purchaseDone &&
      this.purchaseStore.activeStep !== UpsertPurchaseActiveStep.Purchase;
    const showNextButton =
      !this.purchaseStore.purchaseDone &&
      this.purchaseStore.activeStep !== UpsertPurchaseActiveStep.Payment &&
      this.purchaseStore.activeStep !== UpsertPurchaseActiveStep.Purchase;
    const colors = this.purchaseStore.customerFacingCheckOutDetails.getColors();

    const showPaymentComponent =
      this.purchaseStore.customerFacingCheckOutDetails.merchantAccount
        .gateway === "Fiserv"
        ? this.purchaseStore.paymentIntent
        : true;
    const { paymentDetails } = this.purchaseStore.customerFacingCheckOutDetails;

    const activeStepComponent = () => {
      const addOnStepComponent = () => {
        return (
          <div className="flex-auto-grow">
            <PurchaseAddOn
              colors={colors}
              purchaseStore={this.purchaseStore}
              itemName={
                this.purchaseStore.checkOutPurchaseDetails?.item?.name || ""
              }
              addOns={this.purchaseStore.checkOutPurchaseDetails?.addOns || []}
              requiredItems={
                this.purchaseStore.checkOutPurchaseDetails?.item?.getAllowedRequiredItems() ||
                []
              }
              recommendedItems={
                this.purchaseStore.checkOutPurchaseDetails?.item?.getAllowedRecommendedItems() ||
                []
              }
              relatedItems={
                this.purchaseStore.checkOutPurchaseDetails?.item.relatedItems
              }
              updateAddOns={(addOns: PurchaseCheckOutAddOnDetails[]) =>
                this.updateAddOns(addOns)
              }
            />
          </div>
        );
      };
      const optionStepComponent = () => {
        return (
          <div className="flex-auto-grow">
            <PurchaseOptions
              onUpdatePurchaseDetails={(data) =>
                this.purchaseStore.updateCheckOutPurchaseDetails(data)
              }
              purchaseOutDetails={
                this.purchaseStore.checkOutPurchaseDetails ||
                new PurchaseCheckOutDetails()
              }
              // handleNextOrBack={(isNext: boolean) => this.handleBackOrNextStep(isNext)}
              // updateOptionsPageData={(data: OptionSectionModel) =>
              //     this.updateOptionsPageData(data)
              // }
              //optionsPageData={this.purchaseStore.optionSectiondata}
            />
          </div>
        );
      };
      const paymentStepComponent = () => {
        return (
          <div className="flex-auto-grow">
            {showPaymentComponent && (
              <PurchasePayment
                ref={this.formRef}
                allowedPaymentMethods={
                  this.props.purchaseStore.customerFacingCheckOutDetails.options
                    ?.selectedPaymentMethods || []
                }
                merchantAccount={
                  this.props.purchaseStore.customerFacingCheckOutDetails
                    .merchantAccount
                }
                includeSurcharge={this.includeSurcharge()}
                showSurchargeCheckbox={this.showSurchargeCheckbox()}
                surchargePercentage={this.surchargePercentage()}
                isDonation={
                  this.props.purchaseStore.customerFacingCheckOutDetails
                    .isDonationOnly
                }
                seller={
                  this.props.purchaseStore.customerFacingCheckOutDetails.seller
                }
                color={colors}
                isPaymentInProgress={this.state.isPaymentInProgress}
                onPaymentMethodIdUpdate={(paymentMethodId: string) => {
                  this.onNewPaymentMethodIdUpdate(paymentMethodId);
                }}
                paymentIntent={this.purchaseStore.paymentIntent}
                items={[
                  this.purchaseStore.checkOutPurchaseDetails?.item ||
                    new CustomerFacingCheckOutItemModel(),
                ]}
                addOns={
                  this.purchaseStore.checkOutPurchaseDetails?.addOns || []
                }
                // handleNextOrBack={(isNext: boolean) =>
                //   this.handleBackOrNextStep(isNext)
                // }
                // selectedVariantIndex={
                //   this.purchaseStore.optionSectiondata
                //     .selectedVariantIndex
                // }
                updateItems={(items: CustomerFacingCheckOutItemModel[]) =>
                  this.purchaseStore.updateCheckOutPurchaseDetails(
                    new PurchaseCheckOutDetails({
                      ...this.purchaseStore.checkOutPurchaseDetails,
                      item: items[0],
                    }),
                  )
                }
                updateAddOns={(addOns: PurchaseCheckOutAddOnDetails[]) =>
                  this.updateAddOns(addOns)
                }
                payDetails={paymentDetails}
                paymentSectionData={
                  new PurchasePaymentModel({
                    ...this.purchaseStore.paymentSectionData,
                    paymentDetails:
                      this.purchaseStore.customerFacingCheckOutDetails
                        .paymentDetails,
                    promotionRelatedData:
                      paymentDetails.promoCodeSelectionType === "ApplyCode"
                        ? paymentDetails.applyCodePromotionDetails
                        : this.purchaseStore.paymentSectionData
                            .promotionRelatedData,
                  })
                }
                updatePaymentSectionData={(data: PurchasePaymentModel) =>
                  this.purchaseStore.updatePaymentSectionData(data)
                }
                countriesWithState={this.props.countryStore.countriesWithState}
                onPayClick={() => this.onPayClick()}
                accountId={
                  this.purchaseStore.customerFacingCheckOutDetails.accountId
                }
                isPurchaseLink={this.props.isPurchaseLink}
              />
            )}
          </div>
        );
      };
      const successStepComponent = () => {
        // Resolve the receipt description the same way the backend does - from what was actually
        // purchased. Prefer the item name(s), then the checkout name; PurchaseSuccess falls back
        // to the email's generic "Donation" / "Payment" when none is available.
        const checkout = this.purchaseStore.customerFacingCheckOutDetails;
        const itemNames = (checkout?.items || [])
          .map((it) => it?.name?.trim())
          .filter((n): n is string => !!n);
        const receiptDescription =
          itemNames.join(", ") || (checkout?.name?.trim() || "");
        return (
          <div className="flex-auto-grow purchase-confirmed">
            <div>
              <PurchaseSuccess
                purchaseDoneResponse={this.purchaseStore.purchaseDoneResponse}
                afterPayDetails={
                  this.purchaseStore.customerFacingCheckOutDetails
                    .afterPayDetails
                }
                buttonBgColor={
                  this.purchaseStore.customerFacingCheckOutDetails.getColors()
                    ?.primary?.rgba
                }
              />
            </div>
          </div>
        );
      };
      if (this.props.isRenderFromFundraiser) {
        switch (this.purchaseStore.activeStep) {
          case UpsertPurchaseActiveStep.AddOn:
            return addOnStepComponent();
          case UpsertPurchaseActiveStep.Options:
            return optionStepComponent();
          case UpsertPurchaseActiveStep.Payment:
            return paymentStepComponent();
          case UpsertPurchaseActiveStep.Success:
            return successStepComponent();
          default:
            return (
              <div className="flex-auto-grow">
                {this.purchaseSectionComponent}
              </div>
            );
        }
      }

      const renderComponent = (
        currentRoute: UpsertPurchaseActiveStep,
        childrenComponent: ReactNode,
      ) => {
        return (
          <PurchaseProtectedRoute
            checkOutId={this.checkOutId}
            currentRoute={currentRoute}
            purchaseStore={this.purchaseStore}
            isPurchaseLink={this.props.isPurchaseLink}
          >
            {childrenComponent}
          </PurchaseProtectedRoute>
        );
      };
      return (
        <Routes>
          <Route
            path={`/:id`}
            element={renderComponent(
              UpsertPurchaseActiveStep.Purchase,
              this.purchaseSectionComponent,
            )}
          />
          <Route
            path={`/addons/:id?`}
            element={renderComponent(
              UpsertPurchaseActiveStep.AddOn,
              addOnStepComponent(),
            )}
          />
          <Route
            path={`/options/:id?`}
            element={renderComponent(
              UpsertPurchaseActiveStep.Options,
              optionStepComponent(),
            )}
          />
          <Route
            path={`/payment/:id?`}
            element={renderComponent(
              UpsertPurchaseActiveStep.Payment,
              paymentStepComponent(),
            )}
          />
          <Route
            path={`/success/:id`}
            element={renderComponent(
              UpsertPurchaseActiveStep.Success,
              successStepComponent(),
            )}
          />
          <Route
            path="*"
            element={
              <Navigate
                to={`/app/purchase/${this.props.isPurchaseLink ? "purchase-link" : "checkout"}/${this.checkOutId}`}
              />
            }
          />
        </Routes>
      );
    };

    return (
      <PurchaseColoredBackground
        outerBorder={colors?.outerBorder?.rgba}
        outerColor={colors?.outerBackground?.rgba}
        innerBorder={colors?.innerBorder?.rgba}
        innerColor={isPaymentPage ? undefined : colors?.innerBackground?.rgba}
        className={`${
          this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly &&
          this.purchaseStore.customerFacingCheckOutDetails.donationCheckout
            .imagePlacement === ImagePlacementTypes.Background
            ? "colored-bg-with-image"
            : this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly
              ? "purchase-colored-donation-only"
              : ""
        } ${this.classBasedOnActiveStep} `}
        padding={
          this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly &&
          this.purchaseStore.activeStep === UpsertPurchaseActiveStep.Purchase
            ? "0px"
            : "40px 30px"
        }
      >
        <div
          className={`purchase-section ${
            this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly &&
            this.purchaseStore.activeStep !== UpsertPurchaseActiveStep.Payment
              ? "donation-routes-purchase-section"
              : ""
          }`}
        >
          {this.paymentLoader.showLoader ? (
            this.paymentLoader.loaderComponent
          ) : (
            <>
              {activeStepComponent()}
              {(showBackButton || showNextButton) && (
                <div>
                  <FlexContainer
                    justifyContent="center"
                    alignItems="center"
                    spacing={20}
                  >
                    {showBackButton && (
                      <CustomButton
                        // bgColor={colors?.primary?.rgba}
                        bgColor="#fff"
                        borderColor={colors?.primary?.rgba}
                        color={`${colors?.primary?.rgba} !important`}
                        className="purchase-main-btn"
                        onClick={() => this.handleBackOrNextStep(false)}
                        borderRadius={"4px !important"}
                      >
                        Back
                      </CustomButton>
                    )}
                    {showNextButton && (
                      <CustomButton
                        bgColor={colors?.primary?.rgba}
                        borderColor={colors?.primary?.rgba}
                        className="purchase-main-btn"
                        onClick={() => this.handleBackOrNextStep(true)}
                        borderRadius={"4px !important"}
                      >
                        Next
                      </CustomButton>
                    )}
                  </FlexContainer>
                </div>
              )}
            </>
          )}
        </div>
      </PurchaseColoredBackground>
    );
  }

  renderBottomContent = () => {
    return (
      <FlexContainer justifyContent="center" alignItems="center" size={0}>
        <CustomLabel className="purchase-footer">
          Powered by MindMe Payments | Terms | Privacy
        </CustomLabel>
      </FlexContainer>
    );
  };

  hasNonIncludedAddOns() {
    const hasAddOns =
      this.purchaseStore.checkOutPurchaseDetails &&
      this.purchaseStore.checkOutPurchaseDetails?.addOns?.filter(
        (a) => a.isIncludedItem !== true,
      ).length > 0;
    return hasAddOns;
  }

  getNonIncludedAddOns(clonedPurchaseDetails: PurchaseCheckOutDetails) {
    const addons =
      clonedPurchaseDetails &&
      clonedPurchaseDetails?.addOns?.filter((a) => a.isIncludedItem !== true);

    return addons || [];
  }

  handleBackOrNextStep = (isNext: boolean) => {
    const isPurchaseLink = this.props.isPurchaseLink;
    const isDonationOnly =
      this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly;
    const baseRoute = isPurchaseLink
      ? "/app/purchase/purchase-link/"
      : "/app/purchase/checkout/";
    const addOnLink = baseRoute + "addons/" + this.checkOutId;
    const purchaseLink = baseRoute + this.checkOutId;
    const optionsLink = baseRoute + "options/" + this.checkOutId;
    const paymentLink = baseRoute + "payment/" + this.checkOutId;
    const hasAddOns = this.hasNonIncludedAddOns();
    let isValid = true;
    if (isNext && (!isDonationOnly || (isDonationOnly && hasAddOns))) {
      isValid = this.props.purchaseStore?.validate();

      if (this.purchaseStore.activeStep !== UpsertPurchaseActiveStep.Options) {
        if (!isValid) {
          return;
        }
      }
    }

    // const isProduct =
    //   this.purchaseStore.checkOutPurchaseDetails?.item.itemType === "Product";
    const hasVariants =
      this.purchaseStore.checkOutPurchaseDetails?.item.hasVariants;

    switch (this.purchaseStore.activeStep) {
      case UpsertPurchaseActiveStep.Purchase:
        if (this.purchaseStore.showCheckOutAddOns()) {
          this.navigateToURL(addOnLink, UpsertPurchaseActiveStep.AddOn);
        } else {
          //IF DONATION AND DONATION HAS ADD ONS
          if (!isDonationOnly || (isDonationOnly && hasAddOns)) {
            if (hasVariants) {
              this.navigateToURL(optionsLink, UpsertPurchaseActiveStep.Options);
            } else {
              this.redirectToPaymentPage(paymentLink);
            }
          } else {
            this.redirectToPaymentPage(paymentLink);
          }
        }
        break;
      case UpsertPurchaseActiveStep.AddOn:
        if (isNext) {
          if (isDonationOnly && !hasAddOns) {
            this.redirectToPaymentPage(paymentLink);
          } else {
            this.navigateToURL(optionsLink, UpsertPurchaseActiveStep.Options);
          }
        } else {
          this.navigateToURL(purchaseLink, UpsertPurchaseActiveStep.Purchase);
        }
        break;
      case UpsertPurchaseActiveStep.Options:
        this.verifyNavigationFromOptionsPage(
          paymentLink,
          addOnLink,
          purchaseLink,
          isNext,
          isValid,
        );
        break;
      case UpsertPurchaseActiveStep.Payment:
        if (isNext) {
        } else if (isDonationOnly && !hasAddOns) {
          this.navigateToURL(purchaseLink, UpsertPurchaseActiveStep.Purchase);
        } else {
          if (this.purchaseStore.showCheckOutAddOns() || hasVariants) {
            this.navigateToURL(optionsLink, UpsertPurchaseActiveStep.Options);
          } else {
            this.verifyNavigationFromOptionsPage(
              paymentLink,
              addOnLink,
              purchaseLink,
              isNext,
              isValid,
            );
          }
        }
        break;
      default:
        break;
    }
  };

  verifyNavigationFromOptionsPage = (
    paymentLink: string,
    addOnLink: string,
    purchaseLink: string,
    isNext: boolean,
    isValid: boolean,
  ) => {
    const clonedPurchaseDetails = new PurchaseCheckOutDetails({
      ...this.purchaseStore.checkOutPurchaseDetails,
    });
    let activeItemIndex = clonedPurchaseDetails.activeItemIndex;
    const isDonationOnly =
      this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly;
    let move = false;
    if (activeItemIndex === -1) {
      if (this.hasNonIncludedAddOns()) {
        if (isNext) {
          //Allow Navigation for next add onitem
          clonedPurchaseDetails.activeItemIndex = 0;
        } else {
          move = true;
        }
      } else {
        move = true;
      }

      if (!isValid && isNext) {
        if (clonedPurchaseDetails.item.errors?.length > 0) {
          clonedPurchaseDetails.activeItemIndex = -1;
          move = false;
        }
      }
    } else {
      if (clonedPurchaseDetails.addOns?.length > 0) {
        if (isNext) {
          if (
            activeItemIndex ===
            this.getNonIncludedAddOns(clonedPurchaseDetails).length - 1
          ) {
            if (
              !isValid &&
              clonedPurchaseDetails.addOns[activeItemIndex].errors?.length > 0
            ) {
              move = false;
            } else {
              move = true;
            }
          } else {
            const addOnsToRotate = this.getNonIncludedAddOns(
              clonedPurchaseDetails,
            ).filter((a, index) => index > activeItemIndex)[0].id;
            if (
              !isValid &&
              clonedPurchaseDetails.addOns[activeItemIndex].errors?.length > 0
            ) {
              move = false;
              clonedPurchaseDetails.activeItemIndex = activeItemIndex;
            } else {
              activeItemIndex = clonedPurchaseDetails.addOns.findIndex(
                (a) => a.id === addOnsToRotate,
              );

              // clonedPurchaseDetails.activeItemIndex = activeItemIndex + 1;
              clonedPurchaseDetails.activeItemIndex = activeItemIndex;
            }
          }
        } else {
          if (activeItemIndex === -1) {
            move = true;
          } else {
            activeItemIndex = activeItemIndex - 1;
            clonedPurchaseDetails.activeItemIndex = activeItemIndex;
          }
        }
      } else {
        move = true;
      }
    }

    if (isDonationOnly && activeItemIndex === -1 && !isNext) {
      clonedPurchaseDetails.activeItemIndex = 0;
      this.purchaseStore.updateCheckOutPurchaseDetails(clonedPurchaseDetails);
      this.navigateToURL(purchaseLink, UpsertPurchaseActiveStep.Purchase);

      return;
    }

    if (move) {
      if (isNext) {
        this.redirectToPaymentPage(paymentLink);
      } else {
        if (this.purchaseStore.showCheckOutAddOns()) {
          this.navigateToURL(addOnLink, UpsertPurchaseActiveStep.AddOn);
        } else {
          this.navigateToURL(purchaseLink, UpsertPurchaseActiveStep.Purchase);
        }
      }
    } else {
      this.purchaseStore.updateCheckOutPurchaseDetails(clonedPurchaseDetails);
    }
  };

  redirectToPaymentPage = (paymentLink: string) => {
    this.purchaseStore.updateActiveStep(UpsertPurchaseActiveStep.Payment);
    const type = this.props.isRenderFromFundraiser
      ? "None"
      : this.props.isPurchaseLink
        ? "PurchaseLink"
        : "CheckOut";
    if (
      this.purchaseStore.customerFacingCheckOutDetails.merchantAccount
        .gateway === "Fiserv"
    ) {
      this.paymentLoader.show();
      CheckOutService.createPaymentIntent(
        type,
        this.purchaseStore.customerFacingCheckOutDetails.accountId,
        this.checkOutId,
        this.purchaseStore.customerFacingCheckOutDetails.merchantAccount.createAPIModel(),
      ).subscribe({
        next: (result) => {
          if (!result.result) {
            ToastService.showError("Error in creating payment intent");
          } else {
            this.purchaseStore.updatePaymentIntent(
              PurchasePaymentIntentModel.deserializeAPIModel(result.result),
            );
            this.navigateToURL(paymentLink, this.purchaseStore.activeStep);
          }
          this.paymentLoader.hide();
        },
        error: () => {
          ToastService.showError("Error in creating payment intent");
          this.paymentLoader.hide();
        },
      });
    } else {
      this.navigateToURL(paymentLink, this.purchaseStore.activeStep);
    }
  };

  onTabChange = (key: string) => {
    const step = key as UpsertPurchaseActiveStep;
    const isPurchaseLink = this.props.isPurchaseLink;
    const baseRoute = isPurchaseLink
      ? "/app/purchase/purchase-link/"
      : "/app/purchase/checkout/";
    const paymentLink = baseRoute + "payment/" + this.checkOutId;

    const stepToUrl = {
      [UpsertPurchaseActiveStep.Purchase]: `${baseRoute}${this.checkOutId}`,
      [UpsertPurchaseActiveStep.AddOn]: `${baseRoute}addons/${this.checkOutId}`,
      [UpsertPurchaseActiveStep.Options]: `${baseRoute}options/${this.checkOutId}`,
      [UpsertPurchaseActiveStep.Payment]: `${baseRoute}payment/${this.checkOutId}`,
      [UpsertPurchaseActiveStep.Success]: `${baseRoute}payment/${this.checkOutId}`,
    };

    const { isValid, errorStep } =
      this.props.purchaseStore.validatePreviousSteps(step);

    if (!isValid) {
      const stepNames = {
        [UpsertPurchaseActiveStep.Purchase]:
          "Please select an item to purchase",
        [UpsertPurchaseActiveStep.AddOn]: "",
        [UpsertPurchaseActiveStep.Options]: "",
        [UpsertPurchaseActiveStep.Payment]: "Please fill the data",
        [UpsertPurchaseActiveStep.Success]: " ",
      };
      const errorMessage = errorStep ? stepNames[errorStep] : "";
      if (errorStep) {
        if (errorStep === UpsertPurchaseActiveStep.Purchase) {
          ToastService.showError(errorMessage);
        }
        this.navigateToURL(`${stepToUrl[errorStep]}`, errorStep);
      }
      return;
    }

    if (step === UpsertPurchaseActiveStep.Payment) {
      this.redirectToPaymentPage(paymentLink);
      return;
    }
    this.navigateToURL(`${stepToUrl[step]}`, step);
  };

  render(): ReactNode {
    if (!this.props.countryStore.countryDataLoaded) {
      return <>{new Loader().loaderComponent}</>;
    }
    this.setDefaultCountry();
    const colors = this.purchaseStore.customerFacingCheckOutDetails.getColors();
    const isDonationOnly =
      this.purchaseStore.customerFacingCheckOutDetails.isDonationOnly;
    const showLogo = this.props.isPurchaseLink
      ? true
      : this.purchaseStore.customerFacingCheckOutDetails.options.brandingData
          .showLogo;
    const showHelp =
      this.purchaseStore.customerFacingCheckOutDetails.options.brandingData
        .showHelp;
    const showTopBarForDonation = (showLogo || showHelp) && isDonationOnly;
    return (
      <PurchaseWrapper
        bgColor={colors?.pageBackground?.rgba}
        className={showHelp || showLogo ? "purchase-wrapper-logo-help" : ""}
      >
        {!this.purchaseStore.purchaseDone && (
          <div>
            <PurchaseTopBar
              purchaseStore={this.purchaseStore}
              id={this.checkOutId}
              showLogo={showLogo}
              showHelp={showHelp}
              onTabChange={(key: UpsertPurchaseActiveStep) =>
                this.onTabChange(key)
              }
              brandingData={
                this.purchaseStore.customerFacingCheckOutDetails.options
                  ?.brandingData
              }
              activeStep={this.purchaseStore.activeStep}
              seller={this.purchaseStore.customerFacingCheckOutDetails.seller}
            />
          </div>
        )}
        {/* ${this.purchaseStore.customerFacingCheckOutDetails.options.brandingData.showHelp ? "middle-panel-with-topbar" : ""} */}
        <div
          className={`purchase-middle-panel
              ${showTopBarForDonation ? "purchase-donation-topbar-middle" : "purchase-donation-wo-topbar"}`}
        >
          {" "}
          {AccountSettingsStore.isLoadingInProgress || this.loader.showLoader
            ? this.loader.loaderComponent
            : this.routes}
        </div>
        {/* Commented for now */}
        {/* <div>{this.renderBottomContent()}</div> */}
      </PurchaseWrapper>
    );
  }
}

export default withRouter(observer(PurchaseRoutes));
